import React from 'react';
import { FiPayLogo } from './FiPayLogo';
import { OFFICIAL_LINKS } from '../data/servicesData';
import { Phone, MapPin, ExternalLink, ShieldCheck, MessageCircle, ArrowUp, Radio, LogIn } from 'lucide-react';

export const Footer = ({
  onNavigate,
  onOpenInquiry,
}) => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="bg-slate-950 text-slate-300 pt-14 pb-10 border-t border-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Top Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10 pb-10 border-b border-slate-800/80">
          {/* Col 1: Brand Info */}
          <div className="lg:col-span-2 space-y-4">
            <FiPayLogo size="lg" invert />

            <p className="text-xs sm:text-sm text-slate-400 leading-relaxed max-w-sm">
              Fi Pay Bills is India&apos;s premier fintech supporter company empowering businesses with authorized pay-in aggregators, instant payouts, smart POS machines, banking, loans, and IRDA-approved insurance.
            </p>

            {/* Location & Contact without date clutter */}
            <div className="space-y-2 text-xs text-slate-300 pt-1">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-blue-400 shrink-0" />
                <span>{OFFICIAL_LINKS.location}</span>
              </div>
              <div className="flex items-center gap-2">
                <Radio className="w-4 h-4 text-emerald-400 shrink-0" />
                <span>Instant T+0, T+1 payouts are live, multi-lender loan platform is live, and insurance portal is live</span>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-emerald-400 shrink-0" />
                <a href={`tel:${OFFICIAL_LINKS.phone}`} className="hover:text-white font-semibold">
                  +91 {OFFICIAL_LINKS.phone}
                </a>
              </div>
            </div>

            <div className="pt-2">
              <a
                href={OFFICIAL_LINKS.whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold transition-colors"
              >
                <MessageCircle className="w-4 h-4" />
                <span>WhatsApp Instant Help Desk</span>
              </a>
            </div>
          </div>

          {/* Col 2: Solutions */}
          <div className="space-y-3">
            <h4 className="text-xs font-bold uppercase tracking-wider text-white">
              Fintech &amp; Pay-Ins
            </h4>
            <ul className="space-y-2 text-xs text-slate-400">
              <li>
                <button
                  onClick={() => onOpenInquiry('Subpaisa & Payment Gateway')}
                  className="hover:text-white transition-colors text-left cursor-pointer"
                >
                  Subpaisa &amp; Gateway Rails
                </button>
              </li>
              <li>
                <button
                  onClick={() => onOpenInquiry('Apply Razorpay Onboarding')}
                  className="hover:text-white transition-colors text-left cursor-pointer"
                >
                  Apply Razorpay
                </button>
              </li>
              <li>
                <button
                  onClick={() => onOpenInquiry('Apply Cashfree Onboarding')}
                  className="hover:text-white transition-colors text-left cursor-pointer"
                >
                  Apply Cashfree
                </button>
              </li>
              <li>
                <button
                  onClick={() => onOpenInquiry('Instant Payouts & Disbursals')}
                  className="hover:text-white transition-colors text-left cursor-pointer"
                >
                  Instant Payouts (All Banks)
                </button>
              </li>
              <li>
                <button
                  onClick={() => onOpenInquiry('Smart Android POS Machines')}
                  className="hover:text-white transition-colors text-left cursor-pointer"
                >
                  Smart POS &amp; Soundboxes
                </button>
              </li>
            </ul>
          </div>

          {/* Col 3: Loans & Insurance */}
          <div className="space-y-3">
            <h4 className="text-xs font-bold uppercase tracking-wider text-white">
              Loans &amp; Insurance
            </h4>
            <ul className="space-y-2 text-xs text-slate-400">
              <li>
                <button
                  onClick={() => onOpenInquiry('Loan & EMI Calculator')}
                  className="hover:text-blue-400 transition-colors text-left font-semibold text-blue-300 cursor-pointer"
                >
                  ★ Highlighted Loan &amp; EMI Calculator
                </button>
              </li>
              <li>
                <button
                  onClick={() => onOpenInquiry('Business & MSME Loans')}
                  className="hover:text-white transition-colors text-left cursor-pointer"
                >
                  Business &amp; MSME Loans (Up to ₹2Cr)
                </button>
              </li>
              <li>
                <button
                  onClick={() => onOpenInquiry('Personal Loans')}
                  className="hover:text-white transition-colors text-left cursor-pointer"
                >
                  Personal Loans (Up to ₹40L)
                </button>
              </li>
              <li>
                <button
                  onClick={() => onOpenInquiry('Health & Mediclaim Insurance')}
                  className="hover:text-emerald-400 transition-colors text-left font-medium cursor-pointer"
                >
                  Insurance Portal (Live)
                </button>
              </li>
              <li>
                <button
                  onClick={() => onOpenInquiry('Become a Partner (Refer & Earn)')}
                  className="text-amber-400 hover:text-amber-300 font-semibold transition-colors text-left flex items-center gap-1 cursor-pointer"
                >
                  <span>Become a Partner</span>
                  <span className="text-[10px] bg-amber-500/20 px-1.5 py-0.5 rounded border border-amber-500/30">Top %</span>
                </button>
              </li>
            </ul>
          </div>

          {/* Col 4: Validation Kit & Portal Sign In */}
          <div className="space-y-3">
            <h4 className="text-xs font-bold uppercase tracking-wider text-white">
              Validation Kit &amp; Portal
            </h4>
            <ul className="space-y-2 text-xs text-slate-400">
              <li>
                <button
                  onClick={() => onOpenInquiry('Validation Kit (PAN, Aadhaar, Bank Details)')}
                  className="hover:text-emerald-400 transition-colors text-left cursor-pointer text-emerald-300 font-semibold"
                >
                  Validation Kit (PAN, Aadhaar, Bank)
                </button>
              </li>
              <li>
                <button
                  onClick={() => onOpenInquiry('Aadhaar Validation & OTP e-KYC API')}
                  className="hover:text-white transition-colors text-left cursor-pointer"
                >
                  Aadhaar Validation &amp; e-KYC
                </button>
              </li>
              <li>
                <button
                  onClick={() => onOpenInquiry('Bank Details & Penny Drop API')}
                  className="hover:text-white transition-colors text-left cursor-pointer"
                >
                  Bank Account Penny Drop (All Banks)
                </button>
              </li>
              <li className="pt-2">
                <button
                  onClick={() => onNavigate('signin')}
                  className="text-blue-400 hover:text-blue-300 font-bold flex items-center gap-1.5 cursor-pointer"
                >
                  <LogIn className="w-3.5 h-3.5" />
                  <span>Partner / Merchant Sign In</span>
                </button>
              </li>
              <li>
                <button
                  onClick={() => onNavigate('signup')}
                  className="text-slate-400 hover:text-white text-xs block cursor-pointer"
                >
                  Create New Account
                </button>
              </li>
            </ul>
          </div>
        </div>

        {/* Reseller Quick Bar: Apply Razorpay & Apply Cashfree */}
        <div className="py-6 border-b border-slate-800/80 flex flex-col md:flex-row items-center justify-between gap-4 text-xs">
          <div className="flex items-center gap-4 flex-wrap">
            <span className="font-semibold text-slate-400">Official Gateway Links:</span>
            <a
              href={OFFICIAL_LINKS.razorpayReferral}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-400 hover:underline flex items-center gap-1 font-semibold"
            >
              <span>Apply Razorpay</span>
              <ExternalLink className="w-3 h-3" />
            </a>
            <span className="text-slate-700">&bull;</span>
            <a
              href={OFFICIAL_LINKS.cashfreeReferral}
              target="_blank"
              rel="noopener noreferrer"
              className="text-emerald-400 hover:underline flex items-center gap-1 font-semibold"
            >
              <span>Apply Cashfree</span>
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>

          <div className="flex items-center gap-1.5 text-slate-400">
            <ShieldCheck className="w-4 h-4 text-blue-400" />
            <span>Bank-Grade 256-bit AES Encryption &bull; Hyderabad, Telangana</span>
          </div>
        </div>

        {/* Bottom copyright */}
        <div className="pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-500">
          <div>
            &copy; {new Date().getFullYear()} Fi Pay Bills. All rights reserved. 
            Insurance underwritten by IRDA-licensed partners.
          </div>
          <button
            onClick={scrollToTop}
            className="flex items-center gap-1.5 text-slate-400 hover:text-white transition-colors cursor-pointer"
          >
            <span>Back to top</span>
            <ArrowUp className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </footer>
  );
};
